import React, { useState, useEffect } from "react";
import {
  Box,
  Text,
  useColorModeValue,
  Spinner,
  Center,
  HStack,
  Icon,
  VStack,
  Badge,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  List,
  ListItem,
  ListIcon,
  Tooltip,
  Flex,
  Button,
  IconButton,
} from "@chakra-ui/react";
import Card from "../../../../components/card/Card";
import { campaignService } from "../../../../services/campaignService";
import { MdInfo, MdCheckCircle, MdWarning, MdTrendingUp, MdTrendingDown, MdChevronLeft, MdChevronRight } from "react-icons/md";
import dayjs from "dayjs";

export default function CampaignRecommendations() {
  const [loading, setLoading] = useState(true);
  const [performanceData, setPerformanceData] = useState(null);
  const [currentPage, setCurrentPage] = useState(0);
  
  const textColor = useColorModeValue("secondaryGray.900", "white");
  const bgColor = useColorModeValue("white", "navy.800");
  const borderColor = useColorModeValue("gray.200", "whiteAlpha.200");
  
  const ITEMS_PER_PAGE = 3;
  
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const response = await campaignService.getCampaignPerformance();
        
        if (response.success) {
          setPerformanceData(response.data);
          setCurrentPage(0); // Veri değiştiğinde sayfayı sıfırla
        }
      } catch (error) {
        console.error('Kampanya performans verileri getirme hatası:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, []);
  
  if (loading) {
    return (
      <Card h="100%">
        <Text color={textColor} fontSize="lg" fontWeight="700" mb="4">
          Kampanya Önerileri
        </Text>
        <Center py={8}>
          <Spinner color="brand.500" />
        </Center>
      </Card>
    );
  }
  
  if (!performanceData) {
    return (
      <Card h="100%">
        <Text color={textColor} fontSize="lg" fontWeight="700" mb="4">
          Kampanya Önerileri
        </Text>
        <Center py={8}>
          <Text color="gray.500">Veri yüklenemedi</Text>
        </Center>
      </Card>
    );
  }
  
  // Öneriler oluştur
  const recommendations = [];
  
  // 1. Düşük performanslı kampanyalar
  if (performanceData.lowPerformance && performanceData.lowPerformance.length > 0) {
    performanceData.lowPerformance.slice(0, 3).forEach(campaign => {
      recommendations.push({
        type: "warning",
        title: `${campaign.name} - Düşük Performans`,
        description: `Bu kampanya ${campaign.views} görüntülenmeye rağmen sadece ${campaign.usageCount} kez kullanıldı.`,
        suggestions: [
          "İndirim oranını artırmayı düşünün",
          "Kampanya görselini ve açıklamasını gözden geçirin",
          "Kampanya süresini uzatmayı değerlendirin"
        ]
      });
    });
  }
  
  // 2. Yakında biten aktif kampanyalar
  const now = new Date();
  const soonEnding = performanceData.campaigns
    .filter(c => {
      const endDate = new Date(c.endDate);
      const daysUntilEnd = Math.ceil((endDate - now) / (1000 * 60 * 60 * 24));
      return c.isActive && daysUntilEnd > 0 && daysUntilEnd <= 7;
    })
    .slice(0, 2);
  
  soonEnding.forEach(campaign => {
    const endDate = new Date(campaign.endDate);
    const daysUntilEnd = Math.ceil((endDate - now) / (1000 * 60 * 60 * 24));
    recommendations.push({
      type: "info",
      title: `${campaign.name} - Yakında Bitiyor`,
      description: `Bu kampanya ${daysUntilEnd} gün sonra sona erecek.`,
      suggestions: [
        "Kampanyayı uzatmayı düşünün",
        "Son günlerde daha fazla görünürlük sağlayın"
      ]
    });
  });
  
  // 3. En başarılı kampanyalar için öneriler
  if (performanceData.topCampaigns && performanceData.topCampaigns.length > 0) {
    const topCampaign = performanceData.topCampaigns[0];
    if (topCampaign.usageCount > 10) {
      recommendations.push({
        type: "success",
        title: `${topCampaign.name} - Başarılı Kampanya`,
        description: `Bu kampanya ${topCampaign.usageCount} kez kullanıldı ve ${topCampaign.totalRevenue.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}₺ gelir sağladı.`,
        suggestions: [
          "Benzer kampanyalar oluşturmayı düşünün",
          "Bu kampanyanın özelliklerini diğer kampanyalara uygulayın",
          "Kampanya süresini uzatmayı değerlendirin"
        ]
      });
    }
  }
  
  // 4. Hiç kullanılmayan kampanyalar
  const unusedCampaigns = performanceData.campaigns.filter(c => c.usageCount === 0 && c.views > 0);
  if (unusedCampaigns.length > 0) {
    recommendations.push({
      type: "error",
      title: `${unusedCampaigns.length} Kampanya Hiç Kullanılmadı`,
      description: "Bu kampanyalar görüntülenmiş ancak hiç kullanılmamış.",
      suggestions: [
        "İndirim oranlarını gözden geçirin",
        "Kampanya açıklamalarını daha çekici hale getirin"
      ]
    });
  }
  
  // 5. Genel öneriler
  if (performanceData.summary) {
    const avgUsage = performanceData.summary.totalCampaigns > 0
      ? performanceData.summary.totalUsage / performanceData.summary.totalCampaigns
      : 0;
    
    if (avgUsage < 2) {
      recommendations.push({
        type: "warning",
        title: "Genel Performans Düşük",
        description: `Ortalama kampanya kullanımı ${avgUsage.toFixed(1)}. Kampanyalarınızı daha etkili hale getirmek için:`,
        suggestions: [
          "Daha yüksek indirim oranları deneyin",
          "Kampanya sürelerini optimize edin"
        ]
      });
    }
  }
  
  const getAlertStatus = (type) => {
    switch (type) {
      case "success": return "success";
      case "warning": return "warning";
      case "error": return "error";
      default: return "info";
    }
  };
  
  const getAlertIcon = (type) => {
    switch (type) {
      case "success": return MdCheckCircle;
      case "warning": return MdWarning;
      case "error": return MdWarning;
      default: return MdInfo;
    }
  };
  
  return (
    <Card h="100%">
      <Flex direction="column" h="100%" justify="space-between">
        <HStack spacing={2} mb="4">
          <Text color={textColor} fontSize="lg" fontWeight="700">
            Kampanya Önerileri
          </Text>
          <Tooltip 
            label="Kampanya performansınıza göre öneriler" 
            placement="top"
            hasArrow
            bg="gray.700"
            color="white"
            fontSize="sm"
            openDelay={300}
          >
            <Box display="inline-block">
              <Icon as={MdInfo} w="16px" h="16px" color="gray.400" cursor="pointer" />
            </Box>
          </Tooltip>
        </HStack>
      
      {recommendations.length === 0 ? (
        <Center py={8}>
          <VStack spacing={2}>
            <Icon as={MdCheckCircle} boxSize={8} color="green.400" />
            <Text color="gray.500" textAlign="center">
              Tüm kampanyalarınız iyi performans gösteriyor!
            </Text>
          </VStack>
        </Center>
      ) : (
        <>
          <VStack spacing={4} align="stretch">
            {recommendations
              .slice(currentPage * ITEMS_PER_PAGE, (currentPage + 1) * ITEMS_PER_PAGE)
              .map((rec, index) => (
                <Alert
                  key={index}
                  status={getAlertStatus(rec.type)}
                  borderRadius="md"
                  flexDirection="column"
                  alignItems="flex-start"
                >
                  <HStack mb={2}>
                    <AlertIcon as={getAlertIcon(rec.type)} />
                    <AlertTitle fontSize="sm" fontWeight="600">
                      {rec.title}
                    </AlertTitle>
                  </HStack>
                  <AlertDescription fontSize="sm" mb={2}>
                    {rec.description}
                  </AlertDescription>
                  {rec.suggestions && rec.suggestions.length > 0 && (
                    <Box w="100%" pl={6}>
                      <List spacing={1}>
                        {rec.suggestions.map((suggestion, idx) => (
                          <ListItem key={idx} fontSize="xs">
                            <ListIcon as={MdTrendingUp} color={getAlertStatus(rec.type) === "success" ? "green.400" : "orange.400"} />
                            {suggestion}
                          </ListItem>
                        ))}
                      </List>
                    </Box>
                  )}
                </Alert>
              ))}
          </VStack>
          
          {/* Pagination */}
          {recommendations.length > ITEMS_PER_PAGE && (
            <HStack justify="center" mt={4} spacing={2}>
              <IconButton
                icon={<Icon as={MdChevronLeft} />}
                aria-label="Önceki sayfa"
                size="sm"
                onClick={() => setCurrentPage(prev => Math.max(0, prev - 1))}
                isDisabled={currentPage === 0}
                variant="outline"
              />
              <Text fontSize="sm" color={textColor}>
                Sayfa {currentPage + 1} / {Math.ceil(recommendations.length / ITEMS_PER_PAGE)}
              </Text>
              <IconButton
                icon={<Icon as={MdChevronRight} />}
                aria-label="Sonraki sayfa"
                size="sm"
                onClick={() => setCurrentPage(prev => Math.min(Math.ceil(recommendations.length / ITEMS_PER_PAGE) - 1, prev + 1))}
                isDisabled={currentPage >= Math.ceil(recommendations.length / ITEMS_PER_PAGE) - 1}
                variant="outline"
              />
            </HStack>
          )}
        </>
      )}
      </Flex>
    </Card>
  );
}

